(() => {
  const validColours = new Set(['white', 'black', 'oak']);
  const mobilePreview = window.matchMedia('(max-width: 749px)');
  const frameOrder = ['white', 'oak', 'black'];
  const framePanels = ['black', 'white', 'oak', 'black', 'white'];
  const warmQueue = [];
  const queuedCards = new WeakSet();
  let warming = false;
  let pointingCard = null;
  const selectedByProduct = new Map();
  const imageRequests = new Map();
  const heroRequests = new Map();
  const cardReferences = new WeakMap();
  const initialisedCards = new WeakSet();
  const observedRoots = new WeakSet();
  const observedViews = new WeakSet();
  const storagePrefix = 'ltp:collection-frame:';
  const pdpWidths = [246, 493, 600, 713, 823, 990, 1100, 1206, 1346, 1426, 1646, 1946];

  const savedColour = (productId) => {
    if (selectedByProduct.has(productId)) return selectedByProduct.get(productId);
    let colour = 'white';
    try {
      const stored = window.sessionStorage.getItem(storagePrefix + productId);
      if (validColours.has(stored)) colour = stored;
    } catch (_) {
      colour = 'white';
    }
    selectedByProduct.set(productId, colour);
    return colour;
  };

  const saveColour = (productId, colour) => {
    selectedByProduct.set(productId, colour);
    try {
      window.sessionStorage.setItem(storagePrefix + productId, colour);
    } catch (_) {}
  };

  const cardParts = (card) => {
    if (cardReferences.has(card)) return cardReferences.get(card);
    const parts = {
      image: card.querySelector('[data-ltp-frame-card-image]'),
      buttons: new Map(
        Array.from(card.querySelectorAll('[data-ltp-frame-swatch]')).map((button) => [
          button.dataset.ltpFrameSwatch,
          button,
        ])
      ),
      links: Array.from(card.querySelectorAll('.card__heading a[href], [data-ltp-frame-preview-link]')),
      lines: Array.from(card.querySelectorAll('[data-ltp-frame-line]')),
      stage: card.querySelector('.card__inner'),
      near: false,
      suppressNextClick: false,
      token: 0,
      colour: 'white',
      imageReady: Promise.resolve(true),
      hoverTimer: null,
      preview: { enabled: false, track: null, slides: [], gesture: null, epoch: 0, preparing: null, index: 1, touching: false, pointing: false, rebasing: false, settleRaf: null, rebaseRaf: null, resizeObserver: null, width: 0 },
    };
    cardReferences.set(card, parts);
    return parts;
  };

  const updateLinks = (card, parts) => {
    const base = card.dataset.ltpProductUrl;
    if (!base) return;
    const destination = new URL(base, window.location.origin);
    destination.searchParams.delete('frame');
    const href = destination.pathname + destination.search + destination.hash;
    parts.links.forEach((link) => {
      link.href = href;
    });
  };

  const requestImage = (button, sizes, priority = 'low') => {
    if (!button) return Promise.resolve(false);
    const key = button.dataset.ltpFrameSrcset + '|' + sizes;
    if (imageRequests.has(key)) {
      const request = imageRequests.get(key);
      if (priority === 'high') request.image.fetchPriority = 'high';
      return request.ready;
    }
    const preload = new Image();
    preload.fetchPriority = priority;
    preload.sizes = sizes;
    preload.srcset = button.dataset.ltpFrameSrcset;
    preload.src = button.dataset.ltpFrameSrc;
    const ready = (preload.decode ? preload.decode() : new Promise((resolve, reject) => {
      preload.onload = resolve;
      preload.onerror = reject;
    })).then(() => true).catch(() => false);
    imageRequests.set(key, { image: preload, ready });
    return ready;
  };

  const showColour = async (card, colour) => {
    const parts = cardParts(card);
    const button = parts.buttons.get(colour);
    if (!button || !parts.image) return;
    const token = ++parts.token;
    if (colour !== 'white') {
      parts.imageReady = requestImage(button, parts.image.sizes, 'high');
      const loaded = await parts.imageReady;
      if (!loaded || token !== parts.token || !card.isConnected) return;
    } else {
      parts.imageReady = Promise.resolve(true);
    }
    if (token !== parts.token || !card.isConnected) return;
    parts.image.srcset = button.dataset.ltpFrameSrcset;
    parts.image.src = button.dataset.ltpFrameSrc;
    parts.image.alt = button.dataset.ltpFrameAlt || '';
    parts.lines.forEach((line) => {
      line.dataset.active = String(line.dataset.ltpFrameLine === colour);
    });
    card.dataset.ltpFrameCurrent = colour;
  };

  const selectColour = (card, colour, persist) => {
    if (!validColours.has(colour)) return;
    const productId = card.dataset.ltpProductId;
    const parts = cardParts(card);
    if (!productId || !parts.buttons.has(colour)) return;
    parts.colour = colour;
    clearTimeout(parts.hoverTimer);
    if (persist) saveColour(productId, colour);
    parts.buttons.forEach((button, buttonColour) => {
      button.setAttribute('aria-pressed', String(buttonColour === colour));
    });
    updateLinks(card, parts);
    showColour(card, colour);
  };

  // Match the canonical V2 gallery's responsive widths and sizes. The browser
  // chooses the same CDN derivative it would choose for the destination hero.
  const warmPdpHero = async (card, immediate = false) => {
    if (card.dataset.ltpEditorialDestination === 'true') return;
    const parts = cardParts(card);
    const button = parts.buttons.get('white');
    if (!button) return;
    if (!immediate) await parts.imageReady;
    if (!card.isConnected) return;
    const pageWidth = Number(card.dataset.ltpPdpPageWidth);
    const desktopWidth = Number.isFinite(pageWidth) && pageWidth > 100 ? pageWidth : 1200;
    const sizes = `(min-width: ${desktopWidth}px) ${(desktopWidth - 100) * 0.65}px, (min-width: 990px) calc(65vw - 6.5rem), (min-width: 750px) calc((100vw - 6.25rem) / 2), calc(100vw - 2.125rem)`;
    const source = button.dataset.ltpFrameSrc;
    const key = source + '|' + sizes;
    if (heroRequests.has(key)) return heroRequests.get(key);
    const originalWidth = Number(button.dataset.ltpFrameOriginalWidth) || 1946;
    const widths = pdpWidths.filter((width) => width <= originalWidth);
    if (!widths.length) widths.push(originalWidth);
    const derivative = (width) => {
      const url = new URL(source, window.location.href);
      url.searchParams.set('width', width);
      return url.href;
    };
    const preload = new Image();
    preload.fetchPriority = 'high';
    preload.sizes = sizes;
    preload.srcset = widths.map((width) => `${derivative(width)} ${width}w`).join(', ');
    preload.src = derivative(widths[widths.length - 1]);
    const ready = (preload.decode ? preload.decode() : new Promise((resolve, reject) => {
      preload.onload = resolve;
      preload.onerror = reject;
    })).then(() => true).catch(() => false);
    heroRequests.set(key, ready);
    return ready;
  };

  const cardLink = (target) => target.closest?.('[data-ltp-frame-card] .card__heading a[href], [data-ltp-frame-card] [data-ltp-frame-preview-link]');

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse') return;
    const link = cardLink(event.target);
    const card = link?.closest('[data-ltp-frame-card]');
    if (!card || link.contains(event.relatedTarget)) return;
    const parts = cardParts(card);
    clearTimeout(parts.hoverTimer);
    parts.hoverTimer = setTimeout(() => warmPdpHero(card), 120);
  }, { passive: true });

  document.addEventListener('pointerout', (event) => {
    const link = cardLink(event.target);
    const card = link?.closest('[data-ltp-frame-card]');
    if (!card || link.contains(event.relatedTarget)) return;
    clearTimeout(cardParts(card).hoverTimer);
  }, { passive: true });

  document.addEventListener('pointerdown', (event) => {
    const link = cardLink(event.target);
    const card = link?.closest('[data-ltp-frame-card]');
    if (card) warmPdpHero(card, true);
  }, { passive: true });

  document.addEventListener('focusin', (event) => {
    const link = cardLink(event.target);
    const card = link?.closest('[data-ltp-frame-card]');
    if (card) warmPdpHero(card);
  });

  const previewAllowed = (card) => {
    const root = card.closest('.ltp-immersive-collection');
    return mobilePreview.matches && card.isConnected && root && root.dataset.gridView !== 'compact' &&
      !!card.querySelector('[data-ltp-frame-preview-link]');
  };

  const indicator = (card, colour) => {
    const parts = cardParts(card);
    parts.colour = colour;
    card.dataset.ltpFrameCurrent = colour;
    parts.lines.forEach((line) => {
      line.dataset.active = String(line.dataset.ltpFrameLine === colour);
    });
  };

  const rebaseTrack = (parts, index) => {
    const preview = parts.preview;
    const track = preview.track;
    const slide = preview.slides[index];
    if (!track || !slide || preview.touching || preview.pointing) return;
    preview.rebasing = true;
    preview.index = index;
    track.style.scrollSnapType = 'none';
    track.style.scrollBehavior = 'auto';
    track.scrollTo({ left: slide.offsetLeft, behavior: 'instant' });
    track.getBoundingClientRect();
    track.style.removeProperty('scroll-snap-type');
    track.style.removeProperty('scroll-behavior');
    cancelAnimationFrame(preview.rebaseRaf);
    preview.rebaseRaf = requestAnimationFrame(() => {
      preview.rebaseRaf = null;
      preview.rebasing = false;
    });
  };

  const commitPreview = (card) => {
    const parts = cardParts(card);
    const preview = parts.preview;
    if (!preview.enabled || !preview.track || preview.touching || preview.pointing || preview.rebasing) return;
    const nearest = preview.slides.reduce((best, slide, index) => {
      const distance = Math.abs(slide.offsetLeft - preview.track.scrollLeft);
      return distance < best.distance ? { index, distance } : best;
    }, { index: 1, distance: Infinity });
    if (nearest.distance > 2) return;
    preview.index = nearest.index;
    indicator(card, framePanels[nearest.index]);
    if (nearest.index === 0 || nearest.index === 4) {
      rebaseTrack(parts, nearest.index === 0 ? 3 : 1);
    }
  };

  const watchPreviewSettle = (card) => {
    const preview = cardParts(card).preview;
    cancelAnimationFrame(preview.settleRaf);
    preview.settleRaf = null;
    if (!preview.enabled || !preview.track || preview.touching || preview.pointing || preview.rebasing) return;
    let last = preview.track.scrollLeft;
    let stable = 0;
    const check = () => {
      preview.settleRaf = null;
      if (!preview.enabled || !card.isConnected || !preview.track || preview.touching || preview.pointing || preview.rebasing) return;
      const current = preview.track.scrollLeft;
      stable = Math.abs(current - last) < 0.1 ? stable + 1 : 0;
      last = current;
      if (stable >= 3) commitPreview(card);
      else preview.settleRaf = requestAnimationFrame(check);
    };
    preview.settleRaf = requestAnimationFrame(check);
  };

  const prepareTrack = async (card) => {
    const parts = cardParts(card);
    const preview = parts.preview;
    if (!preview.enabled || !previewAllowed(card) || !parts.near) return;
    if (preview.track) return;
    if (preview.preparing) return preview.preparing;
    const epoch = preview.epoch;
    preview.preparing = (async () => {
      const loaded = await Promise.all(['oak', 'black'].map((colour) =>
        requestImage(parts.buttons.get(colour), parts.image.sizes || '100vw')
      ));
      if (!loaded.every(Boolean) || !preview.enabled || epoch !== preview.epoch ||
          !card.isConnected || !previewAllowed(card)) return;
      const track = document.createElement('div');
      track.className = 'ltp-card-frame-track';
      track.setAttribute('aria-hidden', 'true');
      const slides = framePanels.map((colour, index) => {
        const slide = document.createElement('div');
        slide.className = 'ltp-card-frame-slide';
        if (index === 0 || index === 4) slide.setAttribute('aria-hidden', 'true');
        const image = document.createElement('img');
        image.alt = '';
        image.draggable = false;
        image.decoding = 'async';
        image.fetchPriority = 'low';
        image.width = parts.image.width;
        image.height = parts.image.height;
        image.sizes = parts.image.sizes;
        image.srcset = parts.buttons.get(colour).dataset.ltpFrameSrcset;
        image.src = parts.buttons.get(colour).dataset.ltpFrameSrc;
        image.dataset.ltpSlideColour = colour;
        slide.append(image);
        track.append(slide);
        return slide;
      });
      const decoded = await Promise.all(slides.map((slide) => {
        const image = slide.firstElementChild;
        return image.decode ? image.decode().then(() => true).catch(() => false) : Promise.resolve(true);
      }));
      if (!decoded.every(Boolean) || !preview.enabled || epoch !== preview.epoch ||
          !card.isConnected || !previewAllowed(card) || preview.touching || preview.pointing) return;
      preview.slides = slides;
      preview.track = track;
      track.style.visibility = 'hidden';
      card.querySelector('[data-ltp-frame-preview-link]').append(track);
      rebaseTrack(parts, 1);
      preview.width = track.clientWidth;
      track.addEventListener('scroll', () => {
        if (preview.gesture && Math.abs(track.scrollLeft - preview.gesture.scrollLeft) > 5) parts.suppressNextClick = true;
        watchPreviewSettle(card);
      }, { passive: true });
      track.addEventListener('scrollend', () => watchPreviewSettle(card), { passive: true });
      preview.resizeObserver = new ResizeObserver(() => {
        if (!card.isConnected) {
          preview.resizeObserver?.disconnect();
          return;
        }
        if (!preview.touching && !preview.pointing && track.clientWidth !== preview.width) {
          preview.width = track.clientWidth;
          rebaseTrack(parts, Math.max(0, frameOrder.indexOf(parts.colour)) + 1);
        }
      });
      preview.resizeObserver.observe(track);
      track.style.removeProperty('visibility');
      card.dataset.ltpFrameSliding = 'true';
    })();
    try { await preview.preparing; } finally {
      if (epoch === preview.epoch) preview.preparing = null;
    }
  };

  const warmVisibleCards = async () => {
    if (warming) return;
    warming = true;
    while (warmQueue.length && mobilePreview.matches) {
      const card = warmQueue.shift();
      queuedCards.delete(card);
      if (!card.isConnected || !previewAllowed(card) || !cardParts(card).near) continue;
      await prepareTrack(card);
    }
    warming = false;
  };

  const queueWarm = (card) => {
    if (!previewAllowed(card)) return;
    if (!queuedCards.has(card)) {
      queuedCards.add(card);
      warmQueue.push(card);
    }
    warmVisibleCards();
  };

  const previewObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const card = entry.target.closest('[data-ltp-frame-card]');
      if (!card || !card.isConnected) {
        previewObserver.unobserve(entry.target);
        return;
      }
      cardParts(card).near = entry.isIntersecting;
      if (entry.isIntersecting && previewAllowed(card)) queueWarm(card);
    });
  }, { rootMargin: '150px 0px', threshold: 0.01 });

  const syncPreview = (card) => {
    const parts = cardParts(card);
    const preview = parts.preview;
    const enabled = !!previewAllowed(card);
    if (preview.enabled === enabled) return;
    preview.enabled = enabled;
    preview.epoch += 1;
    preview.preparing = null;
    preview.gesture = null;
    preview.touching = false;
    preview.pointing = false;
    if (pointingCard === card) pointingCard = null;
    preview.rebasing = false;
    cancelAnimationFrame(preview.settleRaf);
    cancelAnimationFrame(preview.rebaseRaf);
    preview.settleRaf = null;
    preview.rebaseRaf = null;
    preview.resizeObserver?.disconnect();
    preview.resizeObserver = null;
    preview.track?.remove();
    preview.track = null;
    preview.slides = [];
    delete card.dataset.ltpFrameSliding;
    parts.suppressNextClick = false;
    if (enabled) {
      selectColour(card, 'white', false);
      previewObserver.observe(parts.stage);
    } else {
      previewObserver.unobserve(parts.stage);
      parts.near = false;
      selectColour(card, mobilePreview.matches ? 'white' : savedColour(card.dataset.ltpProductId), false);
    }
  };

  const initialisePreview = (card) => {
    const parts = cardParts(card);
    const preview = parts.preview;
    const link = card.querySelector('[data-ltp-frame-preview-link]');
    if (!link || !parts.stage) return;
    const begin = (x, y) => {
      if (!preview.enabled || !previewAllowed(card)) return;
      parts.suppressNextClick = false;
      cancelAnimationFrame(preview.settleRaf);
      preview.settleRaf = null;
      preview.gesture = { x, y, scrollLeft: preview.track?.scrollLeft || 0 };
      if (!preview.track) queueWarm(card);
    };
    const moved = (x, y) => {
      const gesture = preview.gesture;
      if (gesture && Math.hypot(x - gesture.x, y - gesture.y) > 5) {
        parts.suppressNextClick = true;
      }
    };
    const end = () => {
      preview.gesture = null;
      if (!preview.track) queueWarm(card);
      watchPreviewSettle(card);
    };
    link.addEventListener('touchstart', (event) => {
      if (!preview.enabled) return;
      preview.touching = true;
      const touch = event.touches[0];
      if (touch) begin(touch.clientX, touch.clientY);
    }, { passive: true });
    link.addEventListener('touchmove', (event) => {
      const touch = event.touches[0];
      if (touch) moved(touch.clientX, touch.clientY);
    }, { passive: true });
    link.addEventListener('touchend', (event) => {
      preview.touching = event.touches.length > 0;
      if (!preview.touching) end();
    }, { passive: true });
    link.addEventListener('touchcancel', () => {
      preview.touching = false;
      end();
    }, { passive: true });
    link.addEventListener('pointerdown', (event) => {
      if (!preview.enabled || event.pointerType === 'touch' || event.button !== 0 || event.isPrimary === false) return;
      preview.pointing = true;
      pointingCard = card;
      begin(event.clientX, event.clientY);
    }, { passive: true });
    link.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'touch') moved(event.clientX, event.clientY);
    }, { passive: true });
    link.addEventListener('dragstart', (event) => {
      if (preview.enabled) event.preventDefault();
    });
  };

  const releasePreviewPointer = (event) => {
    if (event.pointerType === 'touch' || !pointingCard) return;
    const card = pointingCard;
    pointingCard = null;
    const preview = cardParts(card).preview;
    preview.pointing = false;
    preview.gesture = null;
    if (!preview.track) queueWarm(card);
    watchPreviewSettle(card);
  };

  window.addEventListener('pointerup', releasePreviewPointer, { passive: true });
  window.addEventListener('pointercancel', releasePreviewPointer, { passive: true });

  document.addEventListener('click', (event) => {
    if (!mobilePreview.matches) return;
    const link = event.target.closest?.('[data-ltp-frame-preview-link]');
    if (!link) return;
    const card = link.closest('[data-ltp-frame-card]');
    if (!card || !previewAllowed(card)) return;
    const parts = cardParts(card);
    if (!parts.suppressNextClick) return;
    parts.suppressNextClick = false;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  mobilePreview.addEventListener('change', () => {
    document.querySelectorAll('[data-ltp-frame-card]').forEach((card) => {
      selectColour(card, mobilePreview.matches ? 'white' : savedColour(card.dataset.ltpProductId), false);
      syncPreview(card);
    });
  });

  const initialiseCards = (root = document) => {
    const cards = root.matches?.('[data-ltp-frame-card]')
      ? [root]
      : root.querySelectorAll('[data-ltp-frame-card]');
    cards.forEach((card) => {
      if (initialisedCards.has(card)) { syncPreview(card); return; }
      initialisedCards.add(card);
      initialisePreview(card);
      selectColour(card, mobilePreview.matches ? 'white' : savedColour(card.dataset.ltpProductId), false);
      syncPreview(card);
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-ltp-frame-swatch]');
    if (!button) return;
    const card = button.closest('[data-ltp-frame-card]');
    if (!card) return;
    event.preventDefault();
    event.stopPropagation();
    selectColour(card, button.dataset.ltpFrameSwatch, true);
  });

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse') return;
    const button = event.target.closest?.('[data-ltp-frame-swatch]');
    const card = button?.closest('[data-ltp-frame-card]');
    if (!card || button.contains(event.relatedTarget)) return;
    requestImage(button, cardParts(card).image?.sizes || '25vw');
  }, { passive: true });

  document.addEventListener('pointerdown', (event) => {
    const button = event.target.closest?.('[data-ltp-frame-swatch]');
    const card = button?.closest('[data-ltp-frame-card]');
    if (!card) return;
    requestImage(button, cardParts(card).image?.sizes || '50vw', 'high');
  }, { passive: true });

  document.addEventListener('focusin', (event) => {
    const button = event.target.closest?.('[data-ltp-frame-swatch]');
    const card = button?.closest('[data-ltp-frame-card]');
    if (!card) return;
    requestImage(button, cardParts(card).image?.sizes || '25vw');
  });

  const observeRoot = (root) => {
    if (observedRoots.has(root)) return;
    observedRoots.add(root);
    initialiseCards(root);
    const view = root.closest('.ltp-immersive-collection');
    if (view && !observedViews.has(view)) {
      observedViews.add(view);
      new MutationObserver(() => {
        view.querySelectorAll('[data-ltp-frame-card]').forEach(syncPreview);
      }).observe(view, { attributes: true, attributeFilter: ['data-grid-view'] });
    }
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.removedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE || node.isConnected) return;
          const removedCards = node.matches('[data-ltp-frame-card]') ? [node] : node.querySelectorAll('[data-ltp-frame-card]');
          removedCards.forEach(syncPreview);
        });
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) initialiseCards(node);
        });
      });
    }).observe(root, { childList: true, subtree: true });
  };

  const start = (scope = document) => {
    const selector = '.product-grid-container, [data-ltp-frame-swatch-root]';
    const roots = scope.matches?.(selector) ? [scope] : scope.querySelectorAll(selector);
    roots.forEach(observeRoot);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', (event) => start(event.target));
})();
